import { Grid } from '@mui/material';
import { ODSButton, ODSColumn, ODSText } from '@telekom-ods/react-ui-kit';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { utils, writeFile } from 'xlsx';
import * as Yup from 'yup';

import Provision from '../../../../api/provision';
import { FormikDateField } from '../../../../components/Formik';
import { getTheme } from '../../../../helpers/theme';
import useApiQuery from '../../../../hooks/useApiQuery';
import useAppContext from '../../../../hooks/useAppContext';
import { enqueueSnackbar } from '../../../../redux/notifier/actions';
import { requestStarted, requestFulfilled, requestFailed } from '../../../../redux/status/actions';

import type { VirtualCloud } from '@/types/cloudTypes';
import type { Product } from '@/types/productTypes';
import type { Instance } from '@/types/reportsTypes';

const [nowDate] = new Date().toISOString().split('T');

export interface CloudReportProps {
  product: Product;
}

interface InstanceDetail {
  instances: Instance[];
  metadata: {
    projects: Record<string, { name: string }>;
    hosts: Record<string, { compute_role: string; cpu_ratio: string; cpu_policy: string }>;
  };
  context: {
    cloud_id: string;
  };
  [key: string]: unknown;
}

interface InstanceDetailsResponse {
  data: InstanceDetail[];
}

const CloudReport = ({ product }: CloudReportProps): React.JSX.Element => {
  const { theme } = useAppContext();
  const intl = useIntl();
  const { data: virtualClouds = [], makeApiCall: getVirtualCloudsCall } = useApiQuery<
    VirtualCloud[]
  >(Provision.fetchVirtualClouds);
  const dispatch = useDispatch();

  useEffect(() => {
    if (product.details.juiceGroup) {
      getVirtualCloudsCall({ juiceGroups: [product.details.juiceGroup] });
    }
  }, [product.details.juiceGroup, getVirtualCloudsCall]);

  const downloadReport = async (
    data: { fromDate: string; toDate: string },
    { setSubmitting }: FormikHelpers<{ fromDate: string; toDate: string }>
  ): Promise<void> => {
    const cloudIds = virtualClouds.map(virtualCloud => virtualCloud.cloud.name);
    try {
      dispatch(requestStarted());

      const constructCloudData = (cloudDetails: InstanceDetail) =>
        cloudDetails.instances.map(instance => ({
          'Project Name': cloudDetails.metadata.projects[instance.project_id]?.name ?? '',
          'Instance Name': instance.name,
          'Instance Id': instance.id,
          'Hypervisor Name': instance.host_name,
          'Hypervisor Type': cloudDetails.metadata.hosts[instance.host_name]?.compute_role ?? '',
          'CPU Allocation Ratio': cloudDetails.metadata.hosts[instance.host_name]?.cpu_ratio ?? '',
          vCPUs: instance.cpus_total,
          'Cpu Policy': cloudDetails.metadata.hosts[instance.host_name]?.cpu_policy ?? '',
          'Cpu Usage (hours)': instance.cpu_usage_hours,
          'Memory GB': instance.ram_gigabytes,
          'RAM Usage (hours)': instance.ram_usage_hours,
          'Root Disk GB': instance.disk_gigabytes,
          'Disk Usage (hours)': instance.disk_usage_hours,
          State: instance.status,
          Flavor: instance.flavor_name,
          'Created At': instance.created_at,
          'Deleted At': instance.deleted_at,
        }));

      const instanceDetails = (await Provision.fetchInstanceDetails({
        juice_group: product.details.juiceGroup,
        cloudIds,
        begin: new Date(data.fromDate).setUTCHours(0, 0, 0),
        end: new Date(data.toDate).setUTCHours(23, 59, 59),
      })) as InstanceDetailsResponse;

      const fetchedInstances = instanceDetails.data.map(detail => detail.instances).flat();
      if (!fetchedInstances.length) {
        dispatch(requestFulfilled());
        dispatch(
          enqueueSnackbar({
            message: 'noUsageData',
            options: { variant: 'info' },
          })
        );
        return;
      }

      const workbook = utils.book_new();
      instanceDetails.data.forEach(cloudDetails => {
        const rows = constructCloudData(cloudDetails);
        const worksheet = utils.json_to_sheet(rows);
        utils.book_append_sheet(workbook, worksheet, cloudDetails.context.cloud_id);
      });

      dispatch(requestFulfilled());
      writeFile(workbook, `cloud-report-${data.fromDate}-${data.toDate}.xls`);
    } catch (error) {
      console.error('Error while downloading the report:', error);
      dispatch(requestFailed());
      dispatch(
        enqueueSnackbar({
          message: 'cloudReportDownloadError',
          options: { variant: 'error' },
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    fromDate: '',
    toDate: '',
  };

  const validationSchema = Yup.object().shape({
    fromDate: Yup.date()
      .max(Yup.ref('toDate'), intl.formatMessage({ id: 'fromDateBeforeToDate' }))
      .required(intl.formatMessage({ id: 'formRequiredError' })),
    toDate: Yup.date()
      .max(new Date(nowDate ?? ''), intl.formatMessage({ id: 'toDateBeforeToday' }))
      .min(Yup.ref('fromDate'), intl.formatMessage({ id: 'toDateAfterFromDate' }))
      .required(intl.formatMessage({ id: 'formRequiredError' })),
  });

  return (
    <ODSColumn
      style={{
        padding: '15px',
      }}
    >
      <ODSText
        as='h4'
        color='black'
        gutterBottom
        mt={3}
      >
        {intl.formatMessage({ id: 'cloudReportDescription' })}
      </ODSText>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={downloadReport}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Grid
              container
              alignItems='center'
              gap={2}
            >
              <Grid
                item
                xs={4}
              >
                <FormikDateField
                  name='fromDate'
                  label={intl.formatMessage({ id: 'from' })}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid
                item
                xs={4}
              >
                <FormikDateField
                  name='toDate'
                  label={intl.formatMessage({ id: 'to' })}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item>
                <ODSButton
                  type='submit'
                  label={intl.formatMessage({ id: 'submit' })}
                  variant={getTheme(theme)}
                  disabled={isSubmitting || !isValid || !dirty}
                  size='small'
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </ODSColumn>
  );
};

export default CloudReport;
